import type { MergePlan } from "../../types";
import { uid } from "../storage";

export interface MergePlannerInput {
  labelA: string;
  pathA: string;
  notesA: string;
  labelB: string;
  pathB: string;
  notesB: string;
}

function scoreNotes(notes: string): number {
  let score = 0;
  if (/wrangler|cloudflare|pages/i.test(notes)) score += 3;
  if (/deploy|production|arkmpyre/i.test(notes)) score += 3;
  if (/vite|next|react/i.test(notes)) score += 2;
  if (/openai|anthropic|clerk|prisma|inngest/i.test(notes)) score -= 4;
  if (/local-first|sovereign|no.?api|offline/i.test(notes)) score += 4;
  if (/lovable|builder|prompt|artifact/i.test(notes)) score += 2;
  return score;
}

export function planMerge(input: MergePlannerInput): MergePlan {
  const scoreA = scoreNotes(input.notesA + input.pathA);
  const scoreB = scoreNotes(input.notesB + input.pathB);

  let base: MergePlan["base"] = "composite";
  let salvage: MergePlan["salvage"] = "both";

  if (scoreA > scoreB + 2) {
    base = "A";
    salvage = "B";
  } else if (scoreB > scoreA + 2) {
    base = "B";
    salvage = "A";
  }

  const featureTransfer: string[] = [];
  const conflictFlags: string[] = [];
  const riskFlags: string[] = [];
  const patchSteps: string[] = [];

  if (/openai|anthropic|clerk|inngest/i.test(input.notesA + input.notesB)) {
    riskFlags.push("Paid or SaaS-locked dependencies detected in candidate notes.");
    patchSteps.push("Strip required API adapters; replace with disabled optional adapters.");
  }

  if (base === "A" || base === "composite") {
    featureTransfer.push(`Preserve deploy wiring from ${input.labelA}`);
  }
  if (salvage === "B" || salvage === "both") {
    featureTransfer.push(`Salvage builder logic and prompts from ${input.labelB}`);
  }

  featureTransfer.push(
    "Merge Prompt Forge + Repo Grader deterministic engines",
    "Wire Export Center downloads",
    "Add Sovereign Runtime status block",
  );

  if (scoreA === scoreB) {
    conflictFlags.push("Candidates evenly matched — composite merge recommended.");
  }

  patchSteps.push(
    "Create safety snapshot branch before edits",
    "Copy salvage modules into base src/lib/engine",
    "Run typecheck + build verification",
    "Generate BOB_BUILD_VERIFICATION.md",
  );

  return {
    id: uid("merge"),
    candidateA: { id: "A", label: input.labelA, path: input.pathA },
    candidateB: { id: "B", label: input.labelB, path: input.pathB },
    base,
    salvage,
    featureTransfer,
    conflictFlags,
    riskFlags,
    patchSteps,
    createdAt: new Date().toISOString(),
  };
}
